import moment from "moment";
import { shiftType, normalizeShiftType } from "services/shiftsService";
import { LOCALIZED_DATE_FORMATS, resolveDateLocale } from "utils/dateConfig";
import {
	calculateWorkTime,
	convertFromUTC,
	fixStartEndIntervals,
	formatSecondsToTime,
} from "utils/datetime";

export const TEMPORARY_EMPLOYEES_PAGE_SIZE = 100;
/** Días del selector de cobertura temporal (1 a 7). */
export const COVERAGE_DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
export const BALANCE_START = "2020-01-01";
const BALANCE_PAGE_SIZE = 100;

export const balanceEndForDay = (day) =>
	moment(day).clone().subtract(1, "day").format("YYYY-MM-DD");

const isActiveOnDay = (startDay, endDay, day) => {
	const current = moment(day).startOf("day");
	if (!current.isValid()) return true;
	if (startDay && moment(startDay).isAfter(current, "day")) return false;
	if (endDay && moment(endDay).isBefore(current, "day")) return false;
	return true;
};

const pushGroup = (named, group, startDay, endDay) => {
	const name = group?.name;
	if (!name) return;
	named.push({
		id: group.id ?? null,
		name,
		startDay,
		endDay,
	});
};

/** Grupos vigentes el día consultado. */
export const getEmployeeGroups = (employee, day) => {
	const named = [];

	if (Array.isArray(employee?.groups)) {
		employee.groups.forEach((group) =>
			pushGroup(named, group, group.start_day, group.end_day)
		);
	}

	if (Array.isArray(employee?.employee_groups)) {
		employee.employee_groups.forEach((item) =>
			pushGroup(named, item.group || item, item.start_day, item.end_day)
		);
	}

	if (employee?.employee_with_group?.group) {
		const item = employee.employee_with_group;
		pushGroup(named, item.group, item.start_day, item.end_day);
	}

	const active = named.filter((item) =>
		isActiveOnDay(item.startDay, item.endDay, day)
	);
	const undated = named.filter((item) => !item.startDay && !item.endDay);
	const source = active.length > 0 ? active : undated;
	const unique = [];
	source.forEach((item) => {
		if (!unique.some((current) => current.name === item.name)) unique.push(item);
	});
	return unique;
};

/** Nombres de grupo vigentes el día consultado. */
export const getEmployeeGroupNames = (employee, day) =>
	getEmployeeGroups(employee, day).map((group) => group.name);

/**
 * Grupos de origen del desplegable. El grupo de la planificación actual no
 * aporta cobertura temporal, así que no se ofrece.
 */
export const originGroupsForCoverage = (groups, planGroupId) => {
	const currentId = Number(planGroupId);
	return (groups || [])
		.filter((group) => group?.name && Number(group.id) !== currentId)
		.sort((a, b) => String(a.name).localeCompare(String(b.name), "es"));
};

/** Fecha de inicio: «28 de septiembre de 2026» en español. */
export const formatCoverageStartLabel = (day, lang) => {
	const locale = resolveDateLocale(lang);
	const pattern = (LOCALIZED_DATE_FORMATS[locale] || LOCALIZED_DATE_FORMATS.es).long;
	return moment(day).locale(locale).format(pattern);
};

export const getEmployeeFunctionName = (employee) =>
	employee?.employee_with_function?.function?.name || "";

/** La función que se ve bajo el nombre del empleado. */
export const employeeMatchesFunction = (employee, functionId) => {
	const expected = Number(functionId);
	if (!Number.isFinite(expected) || expected <= 0) return false;
	const profileId = Number(employee?.employee_with_function?.function?.id);
	return profileId === expected;
};

export const coverageEndDay = (startDay, days) =>
	moment(startDay)
		.add(Math.max(Number(days) || 1, 1) - 1, "day")
		.format("YYYY-MM-DD");

/** Semana ISO que contiene el día (lunes a domingo). */
export const weekBounds = (day) => ({
	start: moment(day).startOf("isoWeek").format("YYYY-MM-DD"),
	end: moment(day).endOf("isoWeek").format("YYYY-MM-DD"),
});

/**
 * Color del balance semanal, alineado con el mockup:
 * poco por encima en verde, desvío medio en naranja, muy por encima o justo por debajo en rojo.
 */
export const balanceTone = (value) => {
	const balance = Number(value);
	if (!Number.isFinite(balance)) return "neutral";
	if (balance >= 0 && balance <= 4) return "ok";
	if ((balance > 4 && balance <= 10) || (balance < -3 && balance >= -8)) {
		return "warn";
	}
	return "bad";
};

export const weekHoursLine = (balance, target) => {
	const hours = Number(balance);
	const goal = Number(target);
	if (!Number.isFinite(hours) || !Number.isFinite(goal) || goal <= 0) return null;
	return { hours: goal + hours, target: goal };
};

const findDayPlan = (planAssignment, day) => {
	const plans = planAssignment?.plans || [];
	return (
		plans.find((plan) => moment(plan.day).isSame(day, "day")) ||
		(plans.length === 1 ? plans[0] : null)
	);
};

/** Turnos reales del día. Las celdas vacías llegan con id -1. */
export const getDayAssignments = (planAssignment, day) =>
	(findDayPlan(planAssignment, day)?.assignments || []).filter(
		(item) => item && item.id !== -1 && item.shift
	);

const isAbsenceOrVacation = (type) => {
	const normalized = normalizeShiftType(type);
	return normalized === shiftType.absence || normalized === shiftType.vacation;
};

/** Ausencia o vacaciones en el día: la fila no se puede seleccionar. */
export const rowHasBlockingShift = (planAssignment, day) =>
	getDayAssignments(planAssignment, day).some((item) =>
		isAbsenceOrVacation(item?.shift?.type)
	);

export const getDayShiftNames = (planAssignment, day) => {
	const names = [];
	getDayAssignments(planAssignment, day).forEach((item) => {
		const name = item.shift?.name;
		if (name && !names.includes(name)) names.push(name);
	});
	return names;
};

const rawIntervals = (assignment) => {
	if (assignment?.time_intervals?.length) return assignment.time_intervals;
	return assignment?.shift?.time_intervals || [];
};

const isWorkLike = (type) => {
	const normalized = normalizeShiftType(type);
	return normalized === shiftType.work || normalized === shiftType.automatic;
};

const displayIntervals = (intervals, type) => {
	if (!intervals?.length) return [];
	const normalized = normalizeShiftType(type);
	if (normalized === shiftType.vacation || normalized === shiftType.absence) {
		return intervals.filter((interval) => interval.start !== interval.end);
	}
	const work = intervals.filter((interval) => interval.time_type?.work_time === true);
	if (work.length) return work;
	return intervals;
};

/** Datos para pintar el turno con el mismo layout que la celda del plan. */
export const buildShiftDisplay = (assignment) => {
	const shift = assignment?.shift;
	if (!shift) return null;

	const intervals = fixStartEndIntervals(rawIntervals(assignment)) || [];
	const shown = displayIntervals(intervals, shift.type);
	const rest = normalizeShiftType(shift.type) === shiftType.rest;
	const hoursLayout = isWorkLike(shift.type) && shown.length > 0;

	return {
		id: assignment.id,
		name: shift.name || "",
		rest,
		hoursLayout,
		background: shift.background_color || "",
		border: shift.border_color || shift.background_color || "",
		intervals: shown.map((interval) => ({
			start: convertFromUTC(interval.start),
			end: convertFromUTC(interval.end),
		})),
		total: hoursLayout
			? formatSecondsToTime(
					calculateWorkTime(shown.filter((interval) => interval.time_type)),
					"hm"
			  )
			: "",
		functionName: assignment.function?.name || "",
		functionColor: assignment.function?.background_color || "",
		badge: shift.name || "",
	};
};

export const unwrapAssignmentRows = (response) => {
	if (Array.isArray(response?.data)) return response.data;
	if (Array.isArray(response)) return response;
	return [];
};

export const getTotalItems = (response, rowCount) => {
	const total =
		response?.pagination?.total_items ??
		response?.pagination?.total ??
		response?.total;
	const parsed = Number(total);
	if (Number.isFinite(parsed) && parsed > 0) return parsed;
	return rowCount;
};

export const indexBalanceByEmployee = (rows) =>
	(rows || []).reduce((acc, row) => {
		const employeeId = row?.employee?.id ?? row?.employee_id;
		if (!employeeId) return acc;
		const value = (row.counters || []).reduce((sum, counter) => {
			const parsed = Number(counter?.value);
			return sum + (Number.isFinite(parsed) ? parsed : 0);
		}, 0);
		acc[employeeId] = (acc[employeeId] || 0) + value;
		return acc;
	}, {});

export const fetchBalanceByEmployee = async (
	accountsService,
	{
		functionId,
		day,
		start,
		end,
		accountSystemId,
		transactionRuleSystemId = 5,
		groupId,
	}
) => {
	const rangeEnd = end || balanceEndForDay(day);
	const rangeStart = start || BALANCE_START;
	if (moment(rangeEnd).isBefore(rangeStart, "day")) return {};

	const rows = [];
	let page = 0;
	let total = null;

	while (page < 100) {
		const response = await accountsService.getAccountAssignments({
			page,
			page_size: BALANCE_PAGE_SIZE,
			start: rangeStart,
			end: rangeEnd,
			transaction_rule_system_id: accountSystemId ? undefined : transactionRuleSystemId,
			account_system_id: accountSystemId,
			function_id: functionId,
			group_id: groupId,
			dont_filter_plans_by_function: true,
		});

		if (response?.ok === false) {
			throw new Error("balance");
		}

		const chunk = unwrapAssignmentRows(response);
		rows.push(...chunk);

		const reported =
			response?.pagination?.total_items ?? response?.pagination?.total;
		if (reported) total = Number(reported);
		if (chunk.length < BALANCE_PAGE_SIZE) break;
		if (total && rows.length >= total) break;
		page += 1;
	}

	return indexBalanceByEmployee(rows);
};
